import React, { useEffect, createContext, useContext, useState } from "react";
import "./App.css";
import {
  Route,
  Routes,
  Navigate
} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import LoginPage from "./pages/Login/LoginPage";
import QuizList from "./pages/Quiz/QuizList";
import QuizStart from "./pages/Quiz/QuizStart";
import QuizResult from "./pages/Quiz/result/QuizResult";
import QuizReview from "./pages/Quiz/QuizReview";
import Profile from "./pages/Profile/ProfilePage";
import AllQuiz from "./pages/Admin/Quiz/AllQuiz";
import { useDevToolsStatus } from "./components/hook/isDevToolOpen";
import { FullScreen, useFullScreenHandle, FullScreenHandle } from "react-full-screen";
import Participants from "./pages/Admin/participants/Participants";
import ParticipantDetails from "./pages/Admin/participants/ParticipantDetails";
import TestDetails from "./pages/Admin/participants/testDetails/TestDetails";
import QuizQuestionNew from "./pages/Quiz/QuizQuestionNew";
import CertificationCliSelect from "./pages/Quiz/CertificationCliSelect";
import UserAnalytics from "./pages/UserAnalytics/UserAnalytics";
import QuizFolders from "./pages/Admin/QuestionFolders/QuestionFolders";
import QuizQuestions from "./pages/Admin/QuestionFolders/QuizQuestions";
import AddNewQuestion from "./pages/Admin/QuestionFolders/AddNewQuestion";
import AdminDashboard from "./pages/Admin/dashboard/AdminDashboard";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import config from "./config.json";
import MainLayout from "./components/common/MainLayout";
import { baseUrl } from "./constants/api";
import { setProfile, logout, RootState } from "./store";

export const FullScreenContext = createContext<{
  handle: FullScreenHandle | null;
  reportChange: (val: boolean) => void;
  state: boolean;
}>({
  handle: null,
  reportChange: () => { },
  state: false
});

const MIN_SCREEN_WIDTH = 1024;

const FullScreenConsumer = () => {
  const { handle, reportChange } = useContext(FullScreenContext);
  const { loggedIn, profile } = useSelector((state: RootState) => state.account);

  const isAdmin = profile?.role?.toUpperCase() === "ADMIN";
  const isCli = profile?.role?.toUpperCase() === "CLI";

  if (!handle) return null;

  return (
    <FullScreen handle={handle} onChange={(val) => reportChange(val)}>
      <Routes>
        {isAdmin ? (
          <>
            <Route
              path="/"
              element={loggedIn ? <MainLayout /> : <Navigate replace to="/sign-in" />}
            >
              <Route index element={<AdminDashboard />} />
              <Route path="ques-folders" element={<QuizFolders />} />
              <Route path="ques-folders/:folder/:subfolder/:page" element={<QuizQuestions />} />
              <Route path="ques-folders/:folder/:subfolder/add-question" element={<AddNewQuestion />} />
              <Route path="ques-folders/:folder/:subfolder/edit-question/:questionId" element={<AddNewQuestion />} />
              <Route path="admin-control" element={<AllQuiz />} />
              <Route path="participants" element={<Participants />} />
              <Route path="profile" element={<Profile />} />
              <Route
                path="participants/:participant_id"
                element={<ParticipantDetails />}
              />
              <Route
                path="appered-test/details/:quiz_id"
                element={<TestDetails />}
              />
              <Route path="quiz/:id/review" element={<QuizReview />} />
            </Route>
          </>
        ) : isCli ? (
          <>
            <Route
              path="/"
              element={loggedIn ? <MainLayout /> : <Navigate replace to="/sign-in" />}
            >
              <Route index element={<Navigate replace to="/participants" />} />
              <Route path="profile" element={<Profile />} />
              <Route path="participants" element={<Participants />} />
              <Route
                path="participants/:participant_id"
                element={<ParticipantDetails />}
              />
              <Route path="quiz/:id/review" element={<QuizReview />} />
            </Route>
          </>
        ) : (
          <>
            <Route
              path="/"
              element={loggedIn ? <MainLayout /> : <Navigate replace to="/sign-in" />}
            >
              <Route index element={<QuizList />} />
              <Route path="profile" element={<Profile />} />
              <Route path="analytics" element={<UserAnalytics />} />
              <Route path="participants" element={<Participants />} />
              <Route path="quiz/:id/review" element={<QuizReview />} />
              <Route path="quiz/:id/">
                <Route index element={<Navigate replace to="start" />} />
                <Route path="select-cli" element={<CertificationCliSelect />} />
                <Route path="start" element={<QuizStart />} />
                <Route path=":questionId" element={<QuizQuestionNew />} />
                <Route path="result" element={<QuizResult />} />
              </Route>
            </Route>
          </>
        )}
        <Route
          path="/sign-in"
          element={!loggedIn ? <LoginPage /> : <Navigate replace to="/" />}
        />
        <Route
          path="/sign-up"
          element={!loggedIn ? <LoginPage /> : <Navigate replace to="/" />}
        />
        <Route path="*" element={<Navigate replace to="/" />} />
      </Routes>
    </FullScreen>
  );
};

function App() {
  const handle = useFullScreenHandle();
  const [state, setState] = useState(false);
  const [isScreenWidthTooSmall, setIsScreenWidthTooSmall] = useState(false);
  const { loggedIn, token } = useSelector((state: RootState) => state.account);
  const dispatch = useDispatch();

  const contextValue = {
    handle: handle,
    reportChange: setState,
    state: state,
  };

  const isDevToolsOpen = useDevToolsStatus();

  useEffect(() => {
    document.title = config.app_name;
    document.documentElement.style.setProperty('--primary-color', config.primary_color || '#5A46A7');
  }, []);

  useEffect(() => {
    if (isDevToolsOpen) {
      // alert("Please Close Dev Tools to use our app !!");
    }
  }, [isDevToolsOpen]);

  useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      setIsScreenWidthTooSmall(currentWidth < MIN_SCREEN_WIDTH);
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!loggedIn || !token) return;

      try {
        const response = await fetch(`${baseUrl}/api/users/get-user-details`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data && data.data) {
            dispatch(setProfile(data.data));
          }
        } else {
          if (response.status === 401) {
            dispatch(logout());
          }
        }
      } catch (error) {
        console.error("Failed to fetch user details", error);
      }
    };

    fetchUserDetails();
  }, [loggedIn, token, dispatch]);

  return (
    <FullScreenContext.Provider value={contextValue}>
      {isScreenWidthTooSmall ? (
        <div className="screen-width-alert">
          <div className="alert-content">
            <h2>Screen Too Small!</h2>
            <p>Please view this website on a larger screen.</p>
          </div>
        </div>
      ) : (
        <>
          <FullScreenConsumer />
          <ToastContainer />
        </>
      )}
    </FullScreenContext.Provider>
  );
}

export default App;
